import { Text, View } from '@react-pdf/renderer'
import { Card, Empty, Form, Input, Space, Tag, Typography } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { MarkdownText } from 'features/reporting/pdfGeneration/components/MarkdownText'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PDF_COLORS } from 'features/reporting/pdfGeneration/constants/colors'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import {
  extractSectionData,
  FfsCalculationTablePDF,
  FfsCalculationTableReact,
  FfsReportDataForExtraction,
} from 'pages/reporting/reports/ffs/components/FfsCalculationTable'
import { getSectionsForPart } from 'pages/reporting/reports/ffs/ffsFieldDefinitions'
import {
  FfsAssessmentResult,
  FfsReportSectionId,
} from 'pages/reporting/reports/ffs/ffsReportData'
import { useFfsReportOps } from 'pages/reporting/reports/ffs/FfsReportProvider'
import {
  FfsLtaReportData,
  isPartFiveReport,
  isPartFourReport,
} from 'pages/reporting/reports/ffs/types'
import {
  checkFlawSizeCriteria,
  checkMawpCriteria,
  checkThicknessCriteria,
} from 'pages/reporting/reports/ffs/utils/analysisResults'
import { MarkdownField } from 'pages/reporting/shared/components/MarkdownField'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useEffect, useMemo } from 'react'

const { Title, Text: AntText } = Typography

/** Helper to build extraction data from an FFS report */
function buildReportDataForExtraction(
  ffsReport: FfsLtaReportData
): FfsReportDataForExtraction {
  // Cast the typed objects to Record types for extraction
  // This is safe because we're only reading values from these objects
  const base: FfsReportDataForExtraction = {
    userParams:
      ffsReport.userParams as unknown as FfsReportDataForExtraction['userParams'],
    dataValues:
      ffsReport.dataValues as unknown as FfsReportDataForExtraction['dataValues'],
    mawp: ffsReport.mawp as unknown as FfsReportDataForExtraction['mawp'],
  }

  if (isPartFourReport(ffsReport)) {
    return {
      ...base,
      calculatedValues:
        ffsReport.calculatedValues as unknown as FfsReportDataForExtraction['calculatedValues'],
      thicknessCriteria:
        ffsReport.thicknessCriteria as unknown as FfsReportDataForExtraction['thicknessCriteria'],
    }
  } else if (isPartFiveReport(ffsReport)) {
    return {
      ...base,
      flawSizeCriteria:
        ffsReport.flawSizeCriteria as unknown as FfsReportDataForExtraction['flawSizeCriteria'],
      finalAcceptabilityCriteria:
        ffsReport.finalAcceptabilityCriteria as unknown as FfsReportDataForExtraction['finalAcceptabilityCriteria'],
    }
  }
  return base
}

/**
 * PDF Component for rendering FFS Calculation Details tables
 */
const FfsCalculationDetailsPDF: React.FC<{
  ffsReport: FfsLtaReportData
}> = ({ ffsReport }) => {
  const sections = getSectionsForPart(ffsReport.ffsPart)
  const reportData = useMemo(
    () => buildReportDataForExtraction(ffsReport),
    [ffsReport]
  )

  return (
    <View style={{ marginTop: 8 }}>
      {sections.map((section) => (
        <FfsCalculationTablePDF
          key={section}
          section={section}
          data={extractSectionData(section, reportData)}
          ffsPart={ffsReport.ffsPart}
          ffsLevel={ffsReport.ffsLevel}
        />
      ))}
    </View>
  )
}

/**
 * React Component for rendering FFS Calculation Details tables
 */
const FfsCalculationDetailsReact: React.FC<{
  ffsReport: FfsLtaReportData
}> = ({ ffsReport }) => {
  const sections = getSectionsForPart(ffsReport.ffsPart)
  const reportData = useMemo(
    () => buildReportDataForExtraction(ffsReport),
    [ffsReport]
  )

  return (
    <div>
      {sections.map((section) => (
        <FfsCalculationTableReact
          key={section}
          section={section}
          data={extractSectionData(section, reportData)}
          ffsPart={ffsReport.ffsPart}
          ffsLevel={ffsReport.ffsLevel}
        />
      ))}
    </div>
  )
}

/**
 * PDF Component for a single Assessment Result
 */
const AssessmentResultPDF: React.FC<{
  result: FfsAssessmentResult
  ffsReport: FfsLtaReportData
  index: number
  jobNumber: string
  revisionNumber: number
  isPrelim: boolean
  onRender: PdfProps['onRender']
}> = ({
  result,
  ffsReport,
  index,
  jobNumber,
  revisionNumber,
  isPrelim,
  onRender,
}) => {
  return (
    <ReportPage
      id={`assessment-${result.indicationId}`}
      size='LETTER'
      wrap={true}
      tracker={{
        sectionId:
          index === 0
            ? FfsReportSectionId.ASSESSMENT_RESULTS
            : `${FfsReportSectionId.ASSESSMENT_RESULTS}-${index}`,
        onRender,
        registerOnce: false,
      }}
      contentStyle={[PDFStyles.content, { paddingBottom: 70 }]}
      header={<PageHeader title={`Assessment Result - Ind. ${index + 1}`} />}
      footer={
        <PageFooter
          jobNumber={jobNumber}
          revisionNumber={revisionNumber}
          prelim={isPrelim}
        />
      }>
      {/* Indication Name */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          Indication Name
        </Text>
        <Text style={{ fontSize: 11 }}>{result.indicationTitle}</Text>
      </View>

      {/* Indication Selection */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          Indication Selection
        </Text>
        {result.indicationSelection ? (
          <View>
            <Text style={{ fontSize: 11, marginBottom: 2 }}>
              X-Range: {result.indicationSelection.x.min.toFixed(3)} -{' '}
              {result.indicationSelection.x.max.toFixed(3)}{' '}
              {result.indicationSelection.x.unit}
            </Text>
            <Text style={{ fontSize: 11 }}>
              Y-Range: {result.indicationSelection.y.min.toFixed(3)} -{' '}
              {result.indicationSelection.y.max.toFixed(3)}{' '}
              {result.indicationSelection.y.unit}
            </Text>
          </View>
        ) : (
          <Text style={{ fontSize: 11, fontStyle: 'italic' }}>
            No selection data available
          </Text>
        )}
      </View>

      {/* Part and Level Details */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          Assessment Details
        </Text>
        <Text style={{ fontSize: 11 }}>
          Part {ffsReport.ffsPart === 'part-4' ? '4' : '5'}, Level{' '}
          {ffsReport.ffsLevel}
        </Text>
      </View>

      {/* FFS Calculation Details - Render calculation tables */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          FFS Calculation Details
        </Text>
        <FfsCalculationDetailsPDF ffsReport={ffsReport} />
      </View>

      {/* Results */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 8 }}>
          Results
        </Text>
        {isPartFourReport(ffsReport) ? (
          <View>
            {/* MAWP Criteria */}
            <View style={{ marginBottom: 12 }}>
              <Text
                style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                MAWP Criteria
              </Text>
              {(() => {
                const mawpCheck = checkMawpCriteria(ffsReport)
                const badgeColor = mawpCheck.pass
                  ? PDF_COLORS.SUCCESS
                  : PDF_COLORS.ERROR
                return (
                  <View>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}>
                      <View
                        style={{
                          backgroundColor: badgeColor,
                          borderRadius: 2,
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          marginRight: 8,
                        }}>
                        <Text
                          style={{
                            color: '#FFFFFF',
                            fontSize: 10,
                            fontWeight: 'bold',
                          }}>
                          {mawpCheck.pass ? 'PASS' : 'FAIL'}
                        </Text>
                      </View>
                      <Text style={{ fontSize: 11 }}>
                        MAWPr {'>='} Design MAWP
                      </Text>
                    </View>
                    <Text style={{ fontSize: 10, marginBottom: 2 }}>
                      MAWPr: {mawpCheck.mawprFormatted}
                    </Text>
                    <Text style={{ fontSize: 10 }}>
                      Design MAWP: {mawpCheck.mawpDesignFormatted}
                    </Text>
                  </View>
                )
              })()}
            </View>

            {/* Thickness Criteria */}
            <View style={{ marginBottom: 8 }}>
              <Text
                style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                Thickness Criteria
              </Text>
              {(() => {
                const thicknessCheck = checkThicknessCriteria(ffsReport)
                const badgeColor = thicknessCheck.pass
                  ? PDF_COLORS.SUCCESS
                  : PDF_COLORS.ERROR
                return (
                  <View>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}>
                      <View
                        style={{
                          backgroundColor: badgeColor,
                          borderRadius: 2,
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          marginRight: 8,
                        }}>
                        <Text
                          style={{
                            color: '#FFFFFF',
                            fontSize: 10,
                            fontWeight: 'bold',
                          }}>
                          {thicknessCheck.pass ? 'PASS' : 'FAIL'}
                        </Text>
                      </View>
                      <Text style={{ fontSize: 11 }}>
                        t_eff {'>='} thickness_criteria_min
                      </Text>
                    </View>
                    <Text style={{ fontSize: 10, marginBottom: 2 }}>
                      t_eff: {thicknessCheck.effectiveMeasuredFormatted}
                    </Text>
                    <Text style={{ fontSize: 10 }}>
                      Min Required:{' '}
                      {thicknessCheck.thicknessCriteriaMinFormatted}
                    </Text>
                  </View>
                )
              })()}
            </View>
          </View>
        ) : isPartFiveReport(ffsReport) ? (
          <View>
            {/* Flaw Size Criteria */}
            <View style={{ marginBottom: 12 }}>
              <Text
                style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                Flaw Size Criteria
              </Text>
              {(() => {
                const flawCheck = checkFlawSizeCriteria(ffsReport)
                const badgeColor = flawCheck.overallPass
                  ? PDF_COLORS.SUCCESS
                  : PDF_COLORS.ERROR
                return (
                  <View>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: 6,
                      }}>
                      <View
                        style={{
                          backgroundColor: badgeColor,
                          borderRadius: 2,
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          marginRight: 8,
                        }}>
                        <Text
                          style={{
                            color: '#FFFFFF',
                            fontSize: 10,
                            fontWeight: 'bold',
                          }}>
                          {flawCheck.overallPass ? 'PASS' : 'FAIL'}
                        </Text>
                      </View>
                    </View>

                    {/* RT Check */}
                    <View style={{ marginBottom: 3 }}>
                      <Text style={{ fontSize: 10, marginBottom: 1 }}>
                        {flawCheck.checks.remainingThicknessRatio.pass
                          ? '✓'
                          : '✗'}{' '}
                        {flawCheck.checks.remainingThicknessRatio.label}
                      </Text>
                      <Text style={{ fontSize: 9, marginLeft: 12 }}>
                        Value:{' '}
                        {flawCheck.checks.remainingThicknessRatio.value?.toFixed(
                          3
                        ) ?? 'N/A'}
                        , Required: {'>='}{' '}
                        {flawCheck.checks.remainingThicknessRatio.threshold}
                      </Text>
                    </View>

                    {/* Min Thickness Check */}
                    <View style={{ marginBottom: 3 }}>
                      <Text style={{ fontSize: 10, marginBottom: 1 }}>
                        {flawCheck.checks.minimumThickness.pass ? '✓' : '✗'}{' '}
                        {flawCheck.checks.minimumThickness.label}
                      </Text>
                      <Text style={{ fontSize: 9, marginLeft: 12 }}>
                        Value:{' '}
                        {flawCheck.checks.minimumThickness.value?.toFixed(3) ??
                          'N/A'}{' '}
                        mm, Required: {'>='}{' '}
                        {flawCheck.checks.minimumThickness.threshold?.toFixed(
                          3
                        ) ?? 'N/A'}{' '}
                        mm
                      </Text>
                    </View>

                    {/* LMSD Check */}
                    <View style={{ marginBottom: 3 }}>
                      <Text style={{ fontSize: 10, marginBottom: 1 }}>
                        {flawCheck.checks.lmsdCriteria.pass ? '✓' : '✗'}{' '}
                        {flawCheck.checks.lmsdCriteria.label}
                      </Text>
                      <Text style={{ fontSize: 9, marginLeft: 12 }}>
                        Value:{' '}
                        {flawCheck.checks.lmsdCriteria.value?.toFixed(3) ??
                          'N/A'}{' '}
                        mm, Required: {'>='}{' '}
                        {flawCheck.checks.lmsdCriteria.threshold?.toFixed(3) ??
                          'N/A'}{' '}
                        mm
                      </Text>
                    </View>
                  </View>
                )
              })()}
            </View>

            {/* MAWP Criteria */}
            <View style={{ marginBottom: 8 }}>
              <Text
                style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                MAWP Criteria
              </Text>
              {(() => {
                const mawpCheck = checkMawpCriteria(ffsReport)
                const badgeColor = mawpCheck.pass
                  ? PDF_COLORS.SUCCESS
                  : PDF_COLORS.ERROR
                return (
                  <View>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}>
                      <View
                        style={{
                          backgroundColor: badgeColor,
                          borderRadius: 2,
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          marginRight: 8,
                        }}>
                        <Text
                          style={{
                            color: '#FFFFFF',
                            fontSize: 10,
                            fontWeight: 'bold',
                          }}>
                          {mawpCheck.pass ? 'PASS' : 'FAIL'}
                        </Text>
                      </View>
                      <Text style={{ fontSize: 11 }}>
                        MAWPr {'>='} Design MAWP
                      </Text>
                    </View>
                    <Text style={{ fontSize: 10, marginBottom: 2 }}>
                      MAWPr: {mawpCheck.mawprFormatted}
                    </Text>
                    <Text style={{ fontSize: 10 }}>
                      Design MAWP: {mawpCheck.mawpDesignFormatted}
                    </Text>
                  </View>
                )
              })()}
            </View>
          </View>
        ) : null}
      </View>

      {/* Conclusion */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          Conclusion
        </Text>
        <View>
          {result.conclusion.targetServiceableDate && (
            <Text style={{ fontSize: 11, marginBottom: 2 }}>
              Target Serviceable Date: {result.conclusion.targetServiceableDate}
            </Text>
          )}
          <Text style={{ fontSize: 11, marginBottom: 4 }}>
            Can Reach Target Date:{' '}
            {result.conclusion.canReachTargetDate === null
              ? 'Not Determined'
              : result.conclusion.canReachTargetDate
                ? 'Yes'
                : 'No'}
          </Text>
          {result.conclusion.summary && (
            <MarkdownText baseFontSize={11}>
              {result.conclusion.summary}
            </MarkdownText>
          )}
        </View>
      </View>

      {/* Assumptions */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          Assumptions
        </Text>
        {result.assumptions ? (
          <MarkdownText baseFontSize={11}>{result.assumptions}</MarkdownText>
        ) : (
          <Text style={{ fontSize: 11, fontStyle: 'italic' }}>
            No assumptions specified
          </Text>
        )}
      </View>

      {/* Recommendations */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
          Recommendations
        </Text>
        {result.recommendations ? (
          <MarkdownText baseFontSize={11}>
            {result.recommendations}
          </MarkdownText>
        ) : (
          <Text style={{ fontSize: 11, fontStyle: 'italic' }}>
            No recommendations specified
          </Text>
        )}
      </View>
    </ReportPage>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, indicationsWithReports } = useFfsReportOps()

  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const assessmentResults = useMemo(
    () => formData.assessmentResults,
    [formData.assessmentResults]
  )

  // Create a map of indication ID to FFS report for quick lookup
  const ffsReportsByIndicationId = useMemo(() => {
    const map = new Map<string, FfsLtaReportData>()
    indicationsWithReports.forEach(({ indication, ffsReport }) => {
      map.set(indication.id, ffsReport)
    })
    return map
  }, [indicationsWithReports])

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  if (!assessmentResults || assessmentResults.length === 0) {
    return null
  }

  return (
    <>
      {assessmentResults.map((result, index) => {
        const ffsReport = ffsReportsByIndicationId.get(result.indicationId)
        if (!ffsReport) return null

        return (
          <AssessmentResultPDF
            key={result.indicationId}
            result={result}
            ffsReport={ffsReport}
            index={index}
            jobNumber={mainInfo.assetId}
            revisionNumber={formData.revisionMetadata.revisionNumber}
            isPrelim={isPrelim}
            onRender={onRender}
          />
        )
      })}
    </>
  )
}

/**
 * React Component for editing a single Assessment Result
 */
const AssessmentResultCard: React.FC<{
  result: FfsAssessmentResult
  ffsReport: FfsLtaReportData
  index: number
  onUpdate: (index: number, updates: Partial<FfsAssessmentResult>) => void
}> = ({ result, ffsReport, index, onUpdate }) => {
  const handleConclusionChange = useCallback(
    (
      field: keyof FfsAssessmentResult['conclusion'],
      value: string | boolean | null
    ) => {
      onUpdate(index, {
        conclusion: {
          ...result.conclusion,
          [field]: value,
        },
      })
    },
    [index, result.conclusion, onUpdate]
  )

  const getResultTag = () => {
    if (result.conclusion.canReachTargetDate === null) {
      return <Tag color='default'>Pending</Tag>
    }
    return result.conclusion.canReachTargetDate ? (
      <Tag color='green'>Pass - Can Reach Target Date</Tag>
    ) : (
      <Tag color='red'>Fail - Cannot Reach Target Date</Tag>
    )
  }

  return (
    <Card
      title={
        <Space>
          <span>Indication: {result.indicationTitle}</span>
          {getResultTag()}
        </Space>
      }
      style={{ marginBottom: 16 }}>
      {/* Indication Selection Display */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Indication Selection</Title>
        {result.indicationSelection ? (
          <Space direction='vertical'>
            <AntText>
              X-Range: {result.indicationSelection.x.min.toFixed(3)} -{' '}
              {result.indicationSelection.x.max.toFixed(3)}{' '}
              {result.indicationSelection.x.unit}
            </AntText>
            <AntText>
              Y-Range: {result.indicationSelection.y.min.toFixed(3)} -{' '}
              {result.indicationSelection.y.max.toFixed(3)}{' '}
              {result.indicationSelection.y.unit}
            </AntText>
          </Space>
        ) : (
          <AntText type='secondary'>No selection data available</AntText>
        )}
      </div>

      {/* Part and Level Details */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Assessment Details</Title>
        <Space direction='vertical'>
          <AntText>
            Part {ffsReport.ffsPart === 'part-4' ? '4' : '5'}, Level{' '}
            {ffsReport.ffsLevel}
          </AntText>
        </Space>
      </div>

      {/* FFS Calculation Details - Render calculation tables */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>FFS Calculation Details</Title>
        <FfsCalculationDetailsReact ffsReport={ffsReport} />
      </div>

      {/* Results */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Results</Title>
        {isPartFourReport(ffsReport) ? (
          <div>
            {/* MAWP Criteria */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ fontSize: 14, marginBottom: 8 }}>
                MAWP Criteria
              </Title>
              {(() => {
                const mawpCheck = checkMawpCriteria(ffsReport)
                return (
                  <div>
                    <Space style={{ marginBottom: 8 }}>
                      <Tag color={mawpCheck.pass ? 'success' : 'error'}>
                        {mawpCheck.pass ? 'PASS' : 'FAIL'}
                      </Tag>
                      <AntText>MAWPr {'>='} Design MAWP</AntText>
                    </Space>
                    <div style={{ marginLeft: 16 }}>
                      <AntText style={{ display: 'block' }}>
                        MAWPr: {mawpCheck.mawprFormatted}
                      </AntText>
                      <AntText style={{ display: 'block' }}>
                        Design MAWP: {mawpCheck.mawpDesignFormatted}
                      </AntText>
                    </div>
                  </div>
                )
              })()}
            </div>

            {/* Thickness Criteria */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ fontSize: 14, marginBottom: 8 }}>
                Thickness Criteria
              </Title>
              {(() => {
                const thicknessCheck = checkThicknessCriteria(ffsReport)
                return (
                  <div>
                    <Space style={{ marginBottom: 8 }}>
                      <Tag color={thicknessCheck.pass ? 'success' : 'error'}>
                        {thicknessCheck.pass ? 'PASS' : 'FAIL'}
                      </Tag>
                      <AntText>t_eff {'>='} thickness_criteria_min</AntText>
                    </Space>
                    <div style={{ marginLeft: 16 }}>
                      <AntText style={{ display: 'block' }}>
                        t_eff: {thicknessCheck.effectiveMeasuredFormatted}
                      </AntText>
                      <AntText style={{ display: 'block' }}>
                        Min Required:{' '}
                        {thicknessCheck.thicknessCriteriaMinFormatted}
                      </AntText>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        ) : isPartFiveReport(ffsReport) ? (
          <div>
            {/* Flaw Size Criteria */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ fontSize: 14, marginBottom: 8 }}>
                Flaw Size Criteria
              </Title>
              {(() => {
                const flawCheck = checkFlawSizeCriteria(ffsReport)
                return (
                  <div>
                    <Space style={{ marginBottom: 12 }}>
                      <Tag color={flawCheck.overallPass ? 'success' : 'error'}>
                        {flawCheck.overallPass ? 'PASS' : 'FAIL'}
                      </Tag>
                    </Space>

                    <div style={{ marginLeft: 16 }}>
                      {/* RT Check */}
                      <div style={{ marginBottom: 8 }}>
                        <AntText style={{ display: 'block', marginBottom: 4 }}>
                          {flawCheck.checks.remainingThicknessRatio.pass
                            ? '✓'
                            : '✗'}{' '}
                          {flawCheck.checks.remainingThicknessRatio.label}
                        </AntText>
                        <AntText
                          type='secondary'
                          style={{
                            display: 'block',
                            marginLeft: 16,
                            fontSize: 12,
                          }}>
                          Value:{' '}
                          {flawCheck.checks.remainingThicknessRatio.value?.toFixed(
                            3
                          ) ?? 'N/A'}
                          , Required: {'>='}{' '}
                          {flawCheck.checks.remainingThicknessRatio.threshold}
                        </AntText>
                      </div>

                      {/* Min Thickness Check */}
                      <div style={{ marginBottom: 8 }}>
                        <AntText style={{ display: 'block', marginBottom: 4 }}>
                          {flawCheck.checks.minimumThickness.pass ? '✓' : '✗'}{' '}
                          {flawCheck.checks.minimumThickness.label}
                        </AntText>
                        <AntText
                          type='secondary'
                          style={{
                            display: 'block',
                            marginLeft: 16,
                            fontSize: 12,
                          }}>
                          Value:{' '}
                          {flawCheck.checks.minimumThickness.value?.toFixed(
                            3
                          ) ?? 'N/A'}{' '}
                          mm, Required: {'>='}{' '}
                          {flawCheck.checks.minimumThickness.threshold?.toFixed(
                            3
                          ) ?? 'N/A'}{' '}
                          mm
                        </AntText>
                      </div>

                      {/* LMSD Check */}
                      <div style={{ marginBottom: 8 }}>
                        <AntText style={{ display: 'block', marginBottom: 4 }}>
                          {flawCheck.checks.lmsdCriteria.pass ? '✓' : '✗'}{' '}
                          {flawCheck.checks.lmsdCriteria.label}
                        </AntText>
                        <AntText
                          type='secondary'
                          style={{
                            display: 'block',
                            marginLeft: 16,
                            fontSize: 12,
                          }}>
                          Value:{' '}
                          {flawCheck.checks.lmsdCriteria.value?.toFixed(3) ??
                            'N/A'}{' '}
                          mm, Required: {'>='}{' '}
                          {flawCheck.checks.lmsdCriteria.threshold?.toFixed(
                            3
                          ) ?? 'N/A'}{' '}
                          mm
                        </AntText>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>

            {/* MAWP Criteria */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ fontSize: 14, marginBottom: 8 }}>
                MAWP Criteria
              </Title>
              {(() => {
                const mawpCheck = checkMawpCriteria(ffsReport)
                return (
                  <div>
                    <Space style={{ marginBottom: 8 }}>
                      <Tag color={mawpCheck.pass ? 'success' : 'error'}>
                        {mawpCheck.pass ? 'PASS' : 'FAIL'}
                      </Tag>
                      <AntText>MAWPr {'>='} Design MAWP</AntText>
                    </Space>
                    <div style={{ marginLeft: 16 }}>
                      <AntText style={{ display: 'block' }}>
                        MAWPr: {mawpCheck.mawprFormatted}
                      </AntText>
                      <AntText style={{ display: 'block' }}>
                        Design MAWP: {mawpCheck.mawpDesignFormatted}
                      </AntText>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        ) : null}
      </div>

      {/* Conclusion */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Conclusion</Title>
        <Form.Item label='Target Serviceable Date'>
          <Input
            value={result.conclusion.targetServiceableDate || ''}
            onChange={(e) =>
              handleConclusionChange('targetServiceableDate', e.target.value)
            }
            placeholder='Enter target serviceable date'
          />
        </Form.Item>
        <Form.Item label='Conclusion Summary'>
          <MarkdownField
            value={result.conclusion.summary}
            onChange={(value) => handleConclusionChange('summary', value)}
            placeholder='Enter conclusion summary...'
            rows={4}
          />
        </Form.Item>
      </div>

      {/* Assumptions */}
      <div style={{ marginBottom: 16 }}>
        <MarkdownField
          label='Assumptions'
          value={result.assumptions}
          onChange={(value) => onUpdate(index, { assumptions: value })}
          placeholder='Enter assumptions from the FFS report...'
          rows={4}
        />
      </div>

      {/* Recommendations */}
      <div>
        <MarkdownField
          label='Recommendations'
          value={result.recommendations}
          onChange={(value) => onUpdate(index, { recommendations: value })}
          placeholder='Enter recommendations from the FFS report...'
          rows={4}
        />
      </div>
    </Card>
  )
}

const RenderReact: React.FC = () => {
  const { formData, indicationsWithReports, handleAssessmentResultsFinish } =
    useFfsReportOps()

  const [assessmentResults, setAssessmentResults] = React.useState<
    FfsAssessmentResult[]
  >(formData.assessmentResults || [])

  // Create a map of indication ID to FFS report for quick lookup
  const ffsReportsByIndicationId = useMemo(() => {
    const map = new Map<string, FfsLtaReportData>()
    indicationsWithReports.forEach(({ indication, ffsReport }) => {
      map.set(indication.id, ffsReport)
    })
    return map
  }, [indicationsWithReports])

  // Initialize assessment results from indications with FFS reports
  useEffect(() => {
    if (indicationsWithReports.length === 0) return

    const existingResultsMap = new Map(
      assessmentResults.map((r) => [r.indicationId, r])
    )

    const updatedResults: FfsAssessmentResult[] = indicationsWithReports.map(
      ({ indication, ffsReport }) => {
        const existing = existingResultsMap.get(indication.id)

        // If we already have results for this indication, preserve user edits
        if (existing) {
          return existing
        }

        // Create new result from indication and FFS report
        // ffsReport is guaranteed to be non-null (indications without FFS reports are filtered out)
        const result = ffsReport.outcome.result
        return {
          indicationId: indication.id,
          indicationTitle: indication.title,
          indicationSelection: indication.selection
            ? {
                x: {
                  min: indication.selection.x.min.num,
                  max: indication.selection.x.max.num,
                  unit: indication.selection.x.min.unit.id,
                },
                y: {
                  min: indication.selection.y.min.num,
                  max: indication.selection.y.max.num,
                  unit: indication.selection.y.min.unit.id,
                },
              }
            : null,
          ffsCalculationDetails: '', // Now rendered dynamically from ffsReport
          conclusion: {
            canReachTargetDate:
              result === 'pass' || result === 'derate'
                ? true
                : result === 'fail'
                  ? false
                  : null,
            targetServiceableDate: null,
            summary:
              result === 'pass'
                ? 'The indication passes FFS assessment and can reach the target serviceable date.'
                : result === 'derate'
                  ? 'The indication passes FFS assessment with derate conditions and can reach the target serviceable date.'
                  : result === 'fail'
                    ? 'The indication fails FFS assessment and cannot reach the target serviceable date without repair.'
                    : 'FFS assessment pending.',
          },
          assumptions: '',
          recommendations: ffsReport.outcome.recommendation || '',
        }
      }
    )

    setAssessmentResults(updatedResults)
  }, [indicationsWithReports, assessmentResults])

  const handleUpdateResult = useCallback(
    (index: number, updates: Partial<FfsAssessmentResult>) => {
      setAssessmentResults((prev) => {
        const newResults = [...prev]
        newResults[index] = { ...newResults[index], ...updates }
        return newResults
      })
    },
    []
  )

  const handleSave = async () => {
    await handleAssessmentResultsFinish(assessmentResults)
  }

  if (indicationsWithReports.length === 0) {
    return (
      <Empty
        description='No indications selected for assessment'
        style={{ padding: 40 }}>
        <AntText type='secondary'>
          Please add indication IDs to generate assessment results.
        </AntText>
      </Empty>
    )
  }

  return (
    <StyledForm layout='vertical'>
      <Title level={4}>Assessment Results</Title>
      <AntText type='secondary' style={{ display: 'block', marginBottom: 16 }}>
        Review and edit the assessment results for each indication. Data is
        pre-populated from the FFS LTA reports.
      </AntText>

      {assessmentResults.map((result, index) => {
        const ffsReport = ffsReportsByIndicationId.get(result.indicationId)
        if (!ffsReport) return null

        return (
          <AssessmentResultCard
            key={result.indicationId}
            result={result}
            ffsReport={ffsReport}
            index={index}
            onUpdate={handleUpdateResult}
          />
        )
      })}

      <Form.Item>
        <CantileverButton
          primary
          onClick={handleSave}
          style={{ width: '100%' }}>
          Save Assessment Results
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const AssessmentResults: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: FfsReportSectionId.ASSESSMENT_RESULTS,
  description: 'Assessment results for each indication with FFS analysis',
  title: 'Assessment Results',
  requiredFields: ['assessmentResults'],
}
